import { useState, useCallback } from 'react';
import { chatWithAgent, streamFromAgent } from '../api/aiSpine';

/**
 * Custom hook for managing chat state and interactions with AI Spine
 * @param {boolean} useStreaming - Whether to use streaming responses
 * @returns {Object} Chat state and methods
 */
export function useChat(useStreaming = false) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const addMessage = useCallback((role, content) => {
    const newMessage = {
      id: Date.now(),
      role,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  const updateLastMessage = useCallback((content) => {
    setMessages(prev => {
      const updated = [...prev];
      if (updated.length > 0) {
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content
        };
      }
      return updated;
    });
  }, []);

  const sendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isLoading) return;

    setError(null);
    addMessage('user', userMessage);
    setIsLoading(true);

    try {
      if (useStreaming) {
        // Streaming mode
        addMessage('assistant', '');
        let fullResponse = '';

        for await (const event of streamFromAgent(userMessage, sessionId)) {
          if (event.type === 'token') {
            fullResponse += event.content;
            updateLastMessage(fullResponse);
          } else if (event.type === 'done' && event.session_id) {
            setSessionId(event.session_id);
          }
        }
      } else {
        // Non-streaming mode
        const response = await chatWithAgent(userMessage, sessionId);
        addMessage('assistant', response.response);

        if (response.session_id) {
          setSessionId(response.session_id);
        }
      }
    } catch (err) {
      setError(err.message);
      addMessage('assistant', 'Sorry, I encountered an error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionId, useStreaming, addMessage, updateLastMessage]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sessionId,
    sendMessage,
    clearChat
  };
}

export default useChat;
